import {Injectable} from '@angular/core';
import {HttpInterceptorService} from '../../../../core/services/http-interceptor.service';
import {Observable} from 'rxjs/Rx';
import {DiagnosticOrder} from '../../classes/DiagnosticOrder';
import {Router, ActivatedRoute} from '@angular/router';
import {WorkflowConfigService} from '../../../../workflow-config/services/workflow-config.service';
import {TestService} from '../test.service';
import * as _ from 'lodash';
import {Store} from '@ngrx/store';
import {getPreviewPatient, getPreviewTestConfiguration} from '@ispm/test/services/preview/preview.selectors';
import {CURATION_ACTIONS, CURATION_NAME} from '../../../../curation/store/curation.actions';
import {ICurationState} from '../../../../curation/store/curation.reducer';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {NotificationStoreHandler} from "@ispm/shared/notification/service/notification-store-handler";
import {PreviewStatus} from "@ispm/test/services/preview/preview-status";
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {TestRunningMode} from "@ispm/test/interfaces/test-running-mode.enum";
import {DiagnosticOrderActionsService} from "@ispm/test/services/diagnostic-order-actions.service";
import { DiagnosticOrderService } from '../diagnostic-order.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import {WorkflowService} from "@ispm/workflow/services/workflow.service";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

const STATE_TO_GO = 'Pathologist/PatientProfile/:patientId/:testId/:revisionId/info';

@Injectable()
export abstract class PreviewService {

  private _RETRY_INTERVAL = 2000;
  private patientUrl: string = '/api/v2/patient';
  private curationStore: Store<ICurationState>;
  private extraData = {
    'mutualLinks': true,
    'workflow': {'activeWorkflow': false},
    'order': {'validateAgainstForms': false}
  };

  constructor(protected workflowConfigService: WorkflowConfigService,
              protected diagnosticOrderService: DiagnosticOrderService,
              protected authService: AuthenticationService,
              protected testService: TestService,
              protected diagnosticOrderActionsService: DiagnosticOrderActionsService,
              protected http: HttpInterceptorService,
              protected router: Router,
              public notificationMessageService: NotificationMessageService,
              protected store: Store<IgenomicsState>,
              protected notificationStoreHandler: NotificationStoreHandler,
              protected coreAC: CoreActionCreatorService,
              protected workflowService: WorkflowService,
              protected routerManagerService: RouterManagerService) {
    this.curationStore = this.store.select('curation') as any;
  }

  public startRunPreview(curationDescriptor: any, curationData: any, curationStatus: string, dbName: string) {

      this.coreAC.setLoader(true);
      Observable.combineLatest(this.curationStore.select(getPreviewPatient), this.curationStore.select(getPreviewTestConfiguration))
      .subscribe(([patient, testConfiguration]) => {
        if (patient && testConfiguration) {
          this.fillDataAndCreateTest(patient, testConfiguration, curationDescriptor, curationData, curationStatus, dbName);
        } else {
          Observable.combineLatest(this.findPreviewPatient(curationDescriptor.patient), this.testService.findTestConfiguration(curationDescriptor.testConfiguration.name))
            .subscribe(([pd, tc]) => {
              if (pd && tc) {
                this.curationStore.dispatch({
                  type: CURATION_ACTIONS.SET_PREVIEW_PATIENT,
                  payload: {patient: pd}
                });
                this.curationStore.dispatch({
                  type: CURATION_ACTIONS.SET_PREVIEW_CONFIGURATIONS,
                  payload: {testConfiguration: tc}
                });
                this.fillDataAndCreateTest(pd, tc, curationDescriptor, curationData, curationStatus, dbName);
              } else {
                this.stopPreviewProcess();
              }
            });
        }

      }).unsubscribe();
  }

  protected abstract setDiagnosticOrderParameters(curationData, curationStatus, knowledgeBase, diagnosticOrder);

  public getFirstJobToLoad(testConfiguration: any) :string {
    return _.get(testConfiguration,'initStepToWait', undefined);
  }

  // create diagnostic order and workflow request for preview test
  private fillDataAndCreateTest(patient: any, testConfiguration: any, curationDescriptor: any,
                                curationData: any, curationStatus: string, dbName: string) {
    this.coreAC.setLoader(true);
    const diagnosticOrder = this.createDiagnosticOrderData(patient, testConfiguration, curationDescriptor, curationData, curationStatus, dbName);

    this.testService.createTest(diagnosticOrder, this.extraData)
      .then(diagnosticOrder => {
        if (diagnosticOrder) {
          const openInNewWin = _.has(testConfiguration, 'draftModeData') && testConfiguration.draftModeData.openInNewWindow === true;
          const patientId = diagnosticOrder.subject;
          const worfkflowId = diagnosticOrder.workflowRequest;
          this.waitAndOpen(patientId, worfkflowId, openInNewWin, testConfiguration);

        } else {
          this.notificationMessageService.error("Can't create test,");
        }
      })
      .catch(err => {
        this.notificationMessageService.error("Can't create test, err= " + err.toString());
      });
  }

  private findPreviewPatient(patientDescriptor: any): Observable<any> {
    const request = this.http.get(this.patientUrl, patientDescriptor)
      .map(data => data.json())
      .map(item => {
        if (!item || item.entry.length === 0) {
          this.notificationMessageService.error('Preview Patient not found');
        }
        return item && item.entry && item.entry.length > 0 ? item.entry[0].resource : undefined;
      })
      .publish();
    request.connect();
    return request;
  }

  private createDiagnosticOrderData(patient, testConfiguration, curationDescriptor: any,
                                    curationData: any,
                                    curationStatus: string,
                                    dbName: string) {
    let diagnosticOrder = new DiagnosticOrder(patient.id, this.authService.user.role, this.authService);

    diagnosticOrder.runningMode = TestRunningMode.DRAFT_MODE;
    diagnosticOrder.performer = this.authService.user.info.id;
    diagnosticOrder.role = this.authService.user.role;
    diagnosticOrder.priority = 'routine';
    diagnosticOrder.recipients = [];
    diagnosticOrder.reason = [];
    diagnosticOrder.organization = patient.managingOrganization ?
      patient.managingOrganization : this.authService.user.organization;

    diagnosticOrder.managingOrganization = diagnosticOrder.organization;
    diagnosticOrder.dateOfTest = undefined;
    diagnosticOrder.identifiers = [];
    diagnosticOrder.testConfigurations = [{
      organization: diagnosticOrder.organization,
      testConfiguration: curationDescriptor.testConfiguration
    }];

    diagnosticOrder.workflowRequestTemplateId = testConfiguration.workflowJobsTemplates[0].reference;

    // set fields for Generic API service
    _.set(diagnosticOrder, 'testType', curationDescriptor.testConfiguration.type);
    _.set(diagnosticOrder, 'identifierFormula', testConfiguration.identifierFormula);
    _.set(diagnosticOrder, 'orderOnBehalf', this.authService.user.organization);

    let identifier = _.find(patient.client_defined_identifier_unique, (descr) => {
      return descr.system === 'MRN';
    });
    if (identifier) {
      _.set(diagnosticOrder, 'apiGenomicOrderPatients',
        [{key: 'MRN', value: identifier.value}]);
    }
    identifier = _.find(this.authService.user.info.identifier, (descr) => {
      return descr.system === 'NPPES';
    });

    _.set(diagnosticOrder, 'apiGenomicOrderAssignees',
      [{key: 'PathologistNPI', value: (identifier?.value) ? identifier.value : ""}]);

    diagnosticOrder = this.setDiagnosticOrderParameters(curationData, curationStatus, dbName, diagnosticOrder);
    return diagnosticOrder;
  }

  private stopPreviewProcess(){
    this.coreAC.setLoader(false);
    this.curationStore.dispatch({
      type: CURATION_ACTIONS.SET_PREVIEW_MODE,
      payload: PreviewStatus.finish
    });
  }


  private openNewWindowAndGo(state: any, openInNewWindow: boolean) {
    if (!openInNewWindow) {
      this.router.navigate([state.url], {queryParams : state.queryParams});
      this.stopPreviewProcess();
      return
    } else {
      let windowReference = window.open('', '_blank');
      this.stopPreviewProcess();
      const goToStateHref = this.routerManagerService.setQueryParamsInURL(state.url, state.queryParams);
      if (goToStateHref && windowReference) {
        let fullUrl = goToStateHref;
        if (fullUrl) {
          windowReference.location.href = fullUrl;
          windowReference.focus();
          return;
        } else {
          windowReference.close();
        }
      }
    }
    this.notificationMessageService.error('Unable to open preview test window');
  }

  private createFullUrl(stageHref: any): string {
    let splitResults = _.split(window.location.href, CURATION_NAME, 1);
    return splitResults.length > 0 ? splitResults[0] + stageHref : undefined;
  }

  private waitAndOpen(patientId: string, worfkflowId: any, openInNewWindow: boolean, testConfiguration: any) {
    this.workflowService.getWorklfowById(worfkflowId)
      .then(fetchedWorkflow => {
        const currentJob = fetchedWorkflow.body.activeJobName ? fetchedWorkflow.body.activeJobName.toLowerCase() : null;
        if (currentJob !== this.getFirstJobToLoad(testConfiguration).toLowerCase()) {
          setTimeout(() => {
            this.waitAndOpen(patientId, worfkflowId, openInNewWindow, testConfiguration);
          }, this._RETRY_INTERVAL);
        } else {
          const params = {
            patientId: patientId,
            testId: fetchedWorkflow.body.id,
            revisionId: fetchedWorkflow.body.activeRevision
          }

          const queryParams = this.routerManagerService.queryParameters ? this.routerManagerService.queryParameters : {};
          queryParams['#'] = 'previewMode';
          const state = {
            url: this.routerManagerService.setParamsInURL(STATE_TO_GO, params),
            queryParams
          };

          this.openNewWindowAndGo(state, openInNewWindow);
        }
      });
  }

  public isDraftMode(): boolean{
    let isDraftMode;
    this.diagnosticOrderActionsService.getState().subscribe(res => {
      isDraftMode = res.order.runningMode === 'DRAFT';
    });
    return isDraftMode;
  }
}
